import type { ArtifactKindDefinition, ArtifactListResponseItem } from "@/client";
import type { ArtifactKindPillTheme } from "./itemKind";

/**
 * Kinds the list can filter by. Keep aligned with
 * {@see AnswerDocumentKindCatalog::REGISTRY_KINDS} in PHP; `board` is displayed
 * but not listed, because the registry stores boards as `other`.
 */
export const ANSWER_REGISTRY_KINDS = [
	"report",
	"deck",
	"dataset",
	"clip",
	"export",
	"other",
	"answer",
] as const;

export type ArtifactRegistryKind = (typeof ANSWER_REGISTRY_KINDS)[number];

export type ArtifactKindDisplay = {
	kind: string;
	label: string;
	/** Material Symbols name (same as {@link icon}). */
	symbol: string;
	icon: string;
	smallSymbol: string;
	colour: string;
	pillTheme: ArtifactKindPillTheme;
};

const PILL_THEME_BY_KIND: Record<string, ArtifactKindPillTheme> = {
	report: "document",
	deck: "other",
	dataset: "success",
	clip: "primary",
	export: "warning",
	other: "muted",
	answer: "muted",
	board: "primary",
};

/** Used until {@link listArtifactKinds} resolves; mirrors the PHP catalog defaults. */
export const FALLBACK_ARTIFACT_KINDS: ArtifactKindDefinition[] = [
	{
		kind: "report",
		label: "Document",
		icon: "description",
		colour: "var(--colour-document)",
		hex: "#1abcbc",
	},
	{
		kind: "deck",
		label: "Presentation",
		icon: "burst_mode",
		colour: "var(--colour-presentation)",
		hex: "#f03b56",
	},
	{
		kind: "dataset",
		label: "Dataset",
		icon: "table_chart",
		colour: "var(--colour-dataset)",
		hex: "#0cb25f",
	},
	{
		kind: "clip",
		label: "Clip",
		icon: "video_library",
		colour: "var(--colour-video)",
		hex: "#9b59b6",
	},
	{
		kind: "export",
		label: "Table",
		icon: "download",
		colour: "var(--colour-export)",
		hex: "#0cb25f",
	},
	{
		kind: "other",
		label: "Other",
		icon: "category",
		colour: "var(--colour-other)",
		hex: "#f03b56",
	},
	{
		kind: "answer",
		label: "Answer",
		icon: "chat",
		colour: "var(--grey-600)",
		hex: "#818991",
	},
	{
		kind: "board",
		label: "Board",
		icon: "space_dashboard",
		colour: "var(--colour-video)",
		hex: "#9b59b6",
	},
];

const CSV_FILE_DISPLAY: ArtifactKindDisplay = {
	kind: "export",
	label: "CSV",
	symbol: "table_chart",
	icon: "table_chart",
	smallSymbol: "table_chart",
	colour: "var(--colour-csv)",
	pillTheme: "success",
};

export function indexArtifactKinds(
	kinds: ArtifactKindDefinition[],
): Record<string, ArtifactKindDefinition> {
	return Object.fromEntries(kinds.map((entry) => [entry.kind, entry]));
}

export function toArtifactKindDisplay(
	entry: ArtifactKindDefinition,
): ArtifactKindDisplay {
	const kind = entry.kind;
	return {
		kind,
		label: entry.label,
		symbol: entry.icon,
		icon: entry.icon,
		smallSymbol: entry.icon,
		colour: entry.colour,
		pillTheme: PILL_THEME_BY_KIND[kind] ?? "muted",
	};
}

export function resolveArtifactKindDisplay(
	catalog: Record<string, ArtifactKindDefinition>,
	kind: string | undefined,
	options?: { isCsvFile?: boolean },
): ArtifactKindDisplay {
	if (options?.isCsvFile) {
		return CSV_FILE_DISPLAY;
	}
	const entry =
		catalog[kind ?? "other"] ??
		catalog.other ??
		FALLBACK_ARTIFACT_KINDS.find((row) => row.kind === "other");
	return toArtifactKindDisplay(entry);
}

export function artifactListItemUsesCsvFile(
	item: ArtifactListResponseItem,
): boolean {
	const extension =
		item.documents?.[0]?.file?.content_type?.split("/")[1] ?? null;
	return extension === "csv";
}

export function kindFilterOptionsFromCatalog(
	catalog: Record<string, ArtifactKindDefinition>,
): { value: ArtifactRegistryKind; label: string }[] {
	return ANSWER_REGISTRY_KINDS.filter((kind) => catalog[kind]).map((kind) => ({
		value: kind,
		label: catalog[kind]?.label ?? kind,
	}));
}
